import type { StateStore } from '@playcastdotio/CoreTypes';
import { makeStateStore } from '@playcastdotio/StateStore';
import type { SessionBinding } from '@playcastdotio/Messaging';

/**
 * Host-side game instance lifecycle. Superset of the API-reported lifecycle
 * (`GameInstanceLifecycle` from Phase 1) — `displayCreating`, `failed`, and
 * `stopping` are host-internal states that map to the API lifecycle for
 * reporting purposes.
 */
export type GameInstanceLifecycle =
  | 'idle'
  | 'displayCreating'
  | 'launching'
  | 'ready'
  | 'occupied'
  | 'cooldown'
  | 'failed'
  | 'restarting'
  | 'stopping';

/**
 * Lifecycle states from which a failure is a LAUNCH failure (the instance
 * never reached `ready`). Failures from these states record the reason into
 * `launchError` (in addition to `failure`); failures from live states are
 * runtime crashes (`failure` only). Android multi-instance host Phase 4.
 */
const PRE_READY_STATUSES: readonly GameInstanceLifecycle[] = ['idle', 'displayCreating', 'launching'];

export type GameInstanceDisplayDetails = {
  virtualDisplayGuid?: string;
  virtualDisplayTargetId?: number;
  virtualDisplayAdapterLuid?: { lowPart: number; highPart: number };
  virtualDisplayGdiDeviceName?: string;
  deviceInstanceId?: string;
  devicePath?: string;
};

/**
 * Signed PlayerSessionContract JWS carried on MATCH_ASSIGNED (Phase 1,
 * pool-match-lifecycle). Stored verbatim — the host NEVER verifies signatures.
 */
export type StoredPlayerContract = {
  playerId: string;
  contract: string;
};

/** Default match cooldown after the last player leaves, before the instance re-enters the pool. */
export const DEFAULT_MATCH_COOLDOWN_MS = 30_000;

/**
 * Frozen camera naming contract (android multi-instance, frozen with C++
 * `ShadowCamName` in `GameInstance.h`): instance k maps to
 * `'ShadowCam' + String(k + 1).padStart(2, '0')`. `ShadowCam00` is reserved
 * for the always-on base stream, so instance 0 maps to `ShadowCam01`. The
 * `instanceLaunched.shadowCamName` echo is truth when present — this helper
 * is the expected-name derivation, not a substitute for the echo.
 */
export const shadowCamNameForInstance = (gameInstanceIndex: number): string =>
  `ShadowCam${String(gameInstanceIndex + 1).padStart(2, '0')}`;

/**
 * Full session record for one game instance on the host. Extends
 * `SessionBinding` (Phase 1) with display details populated by Playjector and
 * lifecycle metadata owned by the store.
 *
 * Android multi-instance path: `shadowCamName` (inherited from
 * `SessionBinding`) carries the camera echo from `instanceLaunched` — the
 * truth for the instance → camera mapping (expected name derivable via
 * `shadowCamNameForInstance`). `launchError` carries the launch failure
 * reason for the android path. The legacy resource fields (`vmId`-style
 * launcherInstanceId, display identity, serial, ports) are unused by the
 * android path and retained for the legacy mumu path.
 */
export type GameInstanceSessionRecord = Omit<SessionBinding, 'virtualDisplayGuid' | 'virtualDisplayTargetId' | 'virtualDisplayAdapterLuid' | 'virtualDisplayGdiDeviceName'> &
  GameInstanceDisplayDetails & {
    campaignId?: string;
    packageName?: string;
    vmName?: string;
    status: GameInstanceLifecycle;
    failure?: string;
    /**
     * Launch-phase failure reason (android multi-instance host Phase 4): set
     * alongside `failure` whenever an instance fails before first reaching
     * `ready` — the `instanceLaunched` error/timeout path included. Drives the
     * report's `'launch_failed'` block reason.
     */
    launchError?: string;
    /**
     * Explicit report override for why the instance is unavailable (android
     * multi-instance host Phase 4). Set by the recovery policy (e.g.
     * `'recovery_exhausted'`); takes precedence over the derived reason.
     * Cleared when the instance returns to `ready` or re-enters `restarting`.
     */
    availabilityBlockReason?: string;
    createdAt: number;
    updatedAt: number;
    /** Active assigned match (pool-match-lifecycle Phase 2). Undefined when free. */
    activeMatchRequestId?: string;
    /** Player ids from the current MATCH_ASSIGNED payload. */
    matchPlayerIds: string[];
    /** Signed contracts from MATCH_ASSIGNED — stored, never verified. */
    playerContracts?: StoredPlayerContract[];
    /** Players currently connected to this instance's session. */
    playerCount: number;
    /** Set while in `cooldown`; when it elapses the instance returns to `ready`. */
    cooldownEndsAt?: number;
  };

export type GameInstanceState = {
  sessions: Record<string, GameInstanceSessionRecord>;
  activeSessionIds: string[];
  lastFailure?: string;
};

export type GameInstanceReservableSession = Omit<
  GameInstanceSessionRecord,
  'status' | 'createdAt' | 'updatedAt' | 'playerCount' | 'matchPlayerIds'
>;

const ALLOWED_TRANSITIONS: Record<GameInstanceLifecycle, readonly GameInstanceLifecycle[]> = {
  idle: ['displayCreating', 'failed', 'stopping'],
  displayCreating: ['launching', 'failed', 'stopping'],
  launching: ['ready', 'failed', 'stopping'],
  ready: ['occupied', 'cooldown', 'failed', 'restarting', 'stopping'],
  occupied: ['ready', 'cooldown', 'failed', 'restarting', 'stopping'],
  cooldown: ['ready', 'occupied', 'failed', 'restarting', 'stopping'],
  failed: ['restarting', 'stopping'],
  restarting: ['ready', 'failed', 'stopping'],
  stopping: [],
};

export class GameInstanceStore {
  private fallbackSessionSequence = 0;

  constructor(
    private readonly stateStore: StateStore<GameInstanceState> = makeStateStore<GameInstanceState>({
      sessions: {},
      activeSessionIds: [],
      lastFailure: undefined,
    }),
    private readonly cooldownMs: number = DEFAULT_MATCH_COOLDOWN_MS,
  ) {}

  public nextFallbackSessionId(): string {
    this.fallbackSessionSequence += 1;
    return `gi-${this.fallbackSessionSequence}`;
  }

  public getState(): GameInstanceState {
    return this.stateStore.currentState;
  }

  public getSession(sessionId: string): GameInstanceSessionRecord | undefined {
    return this.getState().sessions[sessionId];
  }

  public getSessionByIndex(gameInstanceIndex: number): GameInstanceSessionRecord | undefined {
    return this.getSessions().find((s) => s.gameInstanceIndex === gameInstanceIndex);
  }

  public getSessions(): GameInstanceSessionRecord[] {
    return this.getState().activeSessionIds
      .map((sessionId) => this.getState().sessions[sessionId])
      .filter((session): session is GameInstanceSessionRecord => Boolean(session));
  }

  public reserve(sessions: GameInstanceReservableSession[]): GameInstanceSessionRecord[] {
    this.assertUnique(sessions);
    const now = Date.now();
    const records = sessions.map((session): GameInstanceSessionRecord => ({
      ...session,
      status: 'idle',
      playerCount: 0,
      matchPlayerIds: [],
      createdAt: now,
      updatedAt: now,
    }));
    const nextSessions = { ...this.getState().sessions };
    const nextIds = [...this.getState().activeSessionIds];
    for (const record of records) {
      nextSessions[record.sessionId] = record;
      nextIds.push(record.sessionId);
    }
    this.stateStore.updateState({
      sessions: nextSessions,
      activeSessionIds: nextIds,
      lastFailure: undefined,
    });
    return records;
  }

  public transition(
    sessionId: string,
    status: GameInstanceLifecycle,
    failure?: string,
  ): void {
    const session = this.requireSession(sessionId);
    if (!ALLOWED_TRANSITIONS[session.status].includes(status)) {
      throw new Error(`Invalid game instance lifecycle transition: ${session.status} -> ${status}`);
    }
    // Phase 4: a failure before first `ready` is a launch failure — record the
    // reason in `launchError` as well so reporting can distinguish
    // 'launch_failed' from a runtime crash ('instance_failed'). A post-ready
    // failure (incl. a failed restart) clears any stale launch-phase reason so
    // the report always reflects the LATEST failure.
    const isLaunchFailure = status === 'failed' && PRE_READY_STATUSES.includes(session.status);
    const updated = {
      ...session,
      status,
      failure: failure ?? session.failure,
      launchError: status === 'failed'
        ? (isLaunchFailure ? (failure ?? session.launchError) : undefined)
        : session.launchError,
      // `ready` clears all failure markers (recovered instance); entering
      // `restarting` clears the exhaustion override (a restart is in flight).
      ...(status === 'ready'
        ? { failure: undefined, launchError: undefined, availabilityBlockReason: undefined }
        : {}),
      ...(status === 'restarting' ? { availabilityBlockReason: undefined } : {}),
      updatedAt: Date.now(),
    };
    this.stateStore.updateState({
      ...this.getState(),
      sessions: { ...this.getState().sessions, [sessionId]: updated },
      lastFailure: failure ?? this.getState().lastFailure,
    });
  }

  /**
   * Set (or clear) the explicit report availability-block override (android
   * multi-instance host Phase 4 — the recovery policy's `'recovery_exhausted'`).
   */
  public setAvailabilityBlockReason(sessionId: string, reason: string | undefined): void {
    this.requireSession(sessionId);
    this.patchSession(sessionId, { availabilityBlockReason: reason });
  }

  /**
   * Persist the C++ `instanceLaunched` shadowCamName echo onto the record
   * (android multi-instance host Phase 5): the echo is the truth for the
   * instance → camera mapping and takes precedence over the derived expected
   * name written at allocation. A name already held by another session is
   * rejected (logged, no-op) so two instances never resolve the same camera.
   */
  public recordShadowCamEcho(sessionId: string, shadowCamName: string): void {
    this.requireSession(sessionId);
    const holder = this.getSessions().find(
      (session) => session.sessionId !== sessionId && session.shadowCamName === shadowCamName,
    );
    if (holder) {
      console.error(
        `[GameInstanceStore] Rejecting shadowCamName echo "${shadowCamName}" for ${sessionId}: ` +
        `already held by ${holder.sessionId}`,
      );
      return;
    }
    this.patchSession(sessionId, { shadowCamName });
  }

  public updateDisplayDetails(sessionId: string, details: GameInstanceDisplayDetails): void {
    const session = this.requireSession(sessionId);
    this.stateStore.updateState({
      ...this.getState(),
      sessions: {
        ...this.getState().sessions,
        [sessionId]: { ...session, ...details, updatedAt: Date.now() },
      },
    });
  }

  /**
   * Record a MATCH_ASSIGNED against a session. `playerContracts` are stored
   * verbatim and never signature-verified on the host.
   */
  public assignMatch(
    sessionId: string,
    assignment: {
      matchRequestId: string;
      playerIds: string[];
      playerContracts?: StoredPlayerContract[];
    },
  ): void {
    this.requireSession(sessionId);
    this.patchSession(sessionId, {
      activeMatchRequestId: assignment.matchRequestId,
      matchPlayerIds: [...assignment.playerIds],
      playerContracts: assignment.playerContracts ? [...assignment.playerContracts] : undefined,
    });
  }

  /** Clear the active match assignment (match ended or evicted). */
  public clearMatchAssignment(sessionId: string): void {
    this.requireSession(sessionId);
    this.patchSession(sessionId, {
      activeMatchRequestId: undefined,
      matchPlayerIds: [],
      playerContracts: undefined,
    });
  }

  /**
   * A player connected to this instance's session: `playerCount+1` and
   * `ready`/`cooldown` → `occupied` (clearing any pending cooldown).
   * Returns the session's new player count.
   */
  public recordPlayerConnected(sessionId: string): number {
    const session = this.requireSession(sessionId);
    const playerCount = session.playerCount + 1;
    const becomesOccupied = session.status === 'ready' || session.status === 'cooldown';
    this.patchSession(sessionId, {
      playerCount,
      ...(becomesOccupied ? { cooldownEndsAt: undefined } : {}),
    });
    if (becomesOccupied) this.transition(sessionId, 'occupied');
    return playerCount;
  }

  /**
   * A player disconnected from this instance's session: `playerCount−1`
   * (floored at 0). At 0 players an `occupied` session enters `cooldown` with
   * `cooldownEndsAt` set from the configured cooldown duration.
   * Returns the session's remaining player count.
   */
  public recordPlayerDisconnected(sessionId: string): number {
    const session = this.requireSession(sessionId);
    const playerCount = Math.max(0, session.playerCount - 1);
    const entersCooldown = playerCount === 0 && session.status === 'occupied';
    this.patchSession(sessionId, {
      playerCount,
      ...(entersCooldown ? { cooldownEndsAt: Date.now() + this.cooldownMs } : {}),
    });
    if (entersCooldown) this.transition(sessionId, 'cooldown');
    return playerCount;
  }

  /** Cooldown elapsed: `cooldown` → `ready`, clearing `cooldownEndsAt`. */
  public completeCooldown(sessionId: string): void {
    const session = this.requireSession(sessionId);
    if (session.status !== 'cooldown') return;
    this.patchSession(sessionId, { cooldownEndsAt: undefined });
    this.transition(sessionId, 'ready');
  }

  public release(sessionId: string): GameInstanceSessionRecord | undefined {
    const released = this.getSession(sessionId);
    if (!released) return undefined;
    if (released.activeMatchRequestId) {
      // Stale-match guard (D3): a record holding a match must never vanish
      // silently — the caller (or the attached MatchLifecycleManager's
      // released-match detection) owes the API a MATCH_ENDED first.
      console.warn(
        `[GameInstanceStore] Releasing session "${sessionId}" with active match ` +
        `${released.activeMatchRequestId} — a MATCH_ENDED must be emitted for it`,
      );
    }
    const sessions = { ...this.getState().sessions };
    delete sessions[sessionId];
    this.stateStore.updateState({
      ...this.getState(),
      sessions,
      activeSessionIds: this.getState().activeSessionIds.filter((id) => id !== sessionId),
    });
    return released;
  }

  public reset(): void {
    this.stateStore.updateState({ sessions: {}, activeSessionIds: [], lastFailure: undefined });
  }

  public addListener(listener: (state: GameInstanceState) => void): () => void {
    const listenerId = this.stateStore.addListener((state) => listener(state));
    return () => this.stateStore.removeListener(listenerId);
  }

  private requireSession(sessionId: string): GameInstanceSessionRecord {
    const session = this.getSession(sessionId);
    if (!session) throw new Error(`Unknown game instance session: ${sessionId}`);
    return session;
  }

  private patchSession(sessionId: string, patch: Partial<GameInstanceSessionRecord>): void {
    const session = this.requireSession(sessionId);
    this.stateStore.updateState({
      ...this.getState(),
      sessions: {
        ...this.getState().sessions,
        [sessionId]: { ...session, ...patch, updatedAt: Date.now() },
      },
    });
  }

  private assertUnique(incoming: GameInstanceReservableSession[]): void {
    const all = [...this.getSessions(), ...incoming];
    this.assertUniqueField(all, 'sessionId', 'session ID');
    this.assertUniqueField(all, 'gameInstanceIndex', 'game instance index');
    this.assertUniqueField(all, 'shadowCamName', 'ShadowCam');
    this.assertUniqueField(all, 'virtualDisplayName', 'virtual display name');
    this.assertUniqueField(all, 'virtualDisplaySerial', 'virtual display serial');
  }

  private assertUniqueField(
    sessions: GameInstanceReservableSession[],
    field: keyof GameInstanceReservableSession,
    label: string,
  ): void {
    const seen = new Set<unknown>();
    for (const session of sessions) {
      const value = session[field];
      if (value === undefined || value === '') continue;
      if (seen.has(value)) throw new Error(`Duplicate game instance ${label}: ${String(value)}`);
      seen.add(value);
    }
  }
}
